import { type PlayerId, type TeamId, playerId } from "@arcade-hockey/shared";
import type { Vec2 } from "../math/Vec2.js";
import type { SkaterRole } from "./roles.js";
import { tuning } from "./tuning.js";
import type { GoalieState, PlayerState, TeamControlState } from "./types.js";

/**
 * 5v5 center-ice faceoff layout (sim-horizontal coords):
 *   Home goal at x = -length/2; Away goal at x = +length/2.
 *   Home team attacks +x; away team attacks -x.
 *   home-1 / away-1 sit at the centre dot ready to take the faceoff;
 *   wings flank the centre, defensemen sit further back.
 *   All home skaters face +x; all away skaters face -x.
 *   Goalies sit on each goal line, x-centered on net.
 *
 * LW/RW are mirrored across the rink so each player occupies their own side
 * of the ice from their own goalie's POV. HOME LW and AWAY LW are both at
 * +y, which keeps the per-team positioning math symmetrical in the bot
 * layer (see packages/bots/src/positioning/home-position.ts).
 */

type SkaterSpawn = {
  suffix: string;
  role: SkaterRole;
  pos: Vec2;
};

// Per-team skater count, 0–5. Goalies are always present and not part of
// this count. Roles are picked in the order defined by ROLE_PROGRESSION
// below: 1 = C, 2 = C+LD, 3 = C+LD+RW, 4 = C+LD+RW+LW, 5 = all.
export type RosterSizes = { home: number; away: number };

// Role pick order as the skater count grows from 1 to 5. C alone first
// because every faceoff needs a centre; defenseman before second forward
// so a low-count team still has a back line; second forward before second
// defenseman to favour offence at mid counts. See plan
// `let-s-make-some-quality-vivid-galaxy.md` for the user-facing rationale.
export const ROLE_PROGRESSION: ReadonlyArray<SkaterRole> = ["C", "LD", "RW", "LW", "RD"] as const;

const MAX_SKATERS_PER_TEAM = ROLE_PROGRESSION.length;

function filterSpawns(
  spawns: ReadonlyArray<SkaterSpawn>,
  count: number,
): ReadonlyArray<SkaterSpawn> {
  const clamped = Math.max(0, Math.min(MAX_SKATERS_PER_TEAM, count));
  if (clamped === MAX_SKATERS_PER_TEAM) return spawns;
  const allowed = new Set<SkaterRole>(ROLE_PROGRESSION.slice(0, clamped));
  return spawns.filter((s) => allowed.has(s.role));
}

// Centers spawn 2 m back from the dot so the first contested puck is a real
// race rather than an instant body-on-body deadlock. Wings spawn at x = ±4
// so the centre has a clear lane to skate straight up the slot without
// soft-colliding into its own winger (used by the sim-tick "P1 only moves
// home-1" test).
const HOME_SPAWNS: ReadonlyArray<SkaterSpawn> = [
  { suffix: "1", role: "C", pos: { x: -2, y: 0 } },
  { suffix: "2", role: "LW", pos: { x: -4, y: 6 } },
  { suffix: "3", role: "RW", pos: { x: -4, y: -6 } },
  { suffix: "4", role: "LD", pos: { x: -10, y: 3.5 } },
  { suffix: "5", role: "RD", pos: { x: -10, y: -3.5 } },
];

const AWAY_SPAWNS: ReadonlyArray<SkaterSpawn> = [
  { suffix: "1", role: "C", pos: { x: 2, y: 0 } },
  { suffix: "2", role: "LW", pos: { x: 4, y: 6 } },
  { suffix: "3", role: "RW", pos: { x: 4, y: -6 } },
  { suffix: "4", role: "LD", pos: { x: 10, y: 3.5 } },
  { suffix: "5", role: "RD", pos: { x: 10, y: -3.5 } },
];

function makeSkater(team: TeamId, spawn: SkaterSpawn, facing: Vec2): PlayerState {
  return {
    id: playerId(`${team}-${spawn.suffix}`),
    teamId: team,
    position: spawn.role,
    pos: { x: spawn.pos.x, y: spawn.pos.y },
    vel: { x: 0, y: 0 },
    moveDir: { x: 0, y: 0 },
    faceDir: { x: facing.x, y: facing.y },
    stickDir: { x: facing.x, y: facing.y },
    action: "Idle",
    actionTimer: 0,
    stunTicks: 0,
    recoveryTicks: 0,
    stamina: 1.0,
    hasPuck: false,
    possessionQuality: 0,
    shootHoldTicks: 0,
    passHoldTicks: 0,
    receivedPassFromTick: -1,
    isProtecting: false,
    benched: false,
  };
}

export function createInitialPlayers(sizes?: RosterSizes): PlayerState[] {
  const faceTowardAway: Vec2 = { x: 1, y: 0 };
  const faceTowardHome: Vec2 = { x: -1, y: 0 };
  const homeSpawns = sizes === undefined ? HOME_SPAWNS : filterSpawns(HOME_SPAWNS, sizes.home);
  const awaySpawns = sizes === undefined ? AWAY_SPAWNS : filterSpawns(AWAY_SPAWNS, sizes.away);
  return [
    ...homeSpawns.map((s) => makeSkater("home", s, faceTowardAway)),
    ...awaySpawns.map((s) => makeSkater("away", s, faceTowardHome)),
  ];
}

// Reset every skater to its spawn position with all per-tick state cleared.
// Called when transitioning into Faceoff (post-goal or new period) so the
// roster snaps back into its faceoff layout instead of staying wherever
// the previous play ended. Mutates in place — zero allocations.
//
// Goalies are not reset here; their state machine snaps them to the arc
// in front of their net every tick during Play, so they're already in
// position by the time the faceoff fires.
export function resetPlayersToSpawn(players: PlayerState[]): void {
  for (const p of players) {
    // Slice 8 — skip benched skaters (3-on-3 OT). They've been
    // pre-positioned at the bench; resetting them to faceoff spawn
    // would un-bench them mid-period. `unbenchAll` is the inverse.
    if (p.benched) continue;
    const spawns = p.teamId === "home" ? HOME_SPAWNS : AWAY_SPAWNS;
    // Match the spawn entry by suffix. The id format is `<team>-<suffix>`.
    const suffixStart = p.id.indexOf("-") + 1;
    const suffix = p.id.slice(suffixStart);
    const spawn = spawns.find((s) => s.suffix === suffix);
    if (spawn === undefined) continue;
    p.pos.x = spawn.pos.x;
    p.pos.y = spawn.pos.y;
    p.vel.x = 0;
    p.vel.y = 0;
    p.moveDir.x = 0;
    p.moveDir.y = 0;
    // Face toward the enemy goal so faceoff looks like a faceoff.
    const fx = p.teamId === "home" ? 1 : -1;
    p.faceDir.x = fx;
    p.faceDir.y = 0;
    p.stickDir.x = fx;
    p.stickDir.y = 0;
    p.action = "Idle";
    p.actionTimer = 0;
    p.stunTicks = 0;
    p.recoveryTicks = 0;
    p.stamina = 1.0;
    p.hasPuck = false;
    p.possessionQuality = 0;
    p.shootHoldTicks = 0;
    p.passHoldTicks = 0;
    p.receivedPassFromTick = -1;
    p.isProtecting = false;
  }
}

export function createInitialGoalies(): GoalieState[] {
  const halfLength = tuning.rink.length / 2;
  const goalLineOffset = tuning.rink.goalLineOffset;

  return [
    {
      id: playerId("goalie-home"),
      teamId: "home",
      pos: { x: -halfLength + goalLineOffset + tuning.goalie.arcDepthFar, y: 0 },
      vel: { x: 0, y: 0 },
      facing: { x: 1, y: 0 },
      mode: "Ready",
      modeTimer: 0,
      pulled: false,
    },
    {
      id: playerId("goalie-away"),
      teamId: "away",
      pos: { x: halfLength - goalLineOffset - tuning.goalie.arcDepthFar, y: 0 },
      vel: { x: 0, y: 0 },
      facing: { x: -1, y: 0 },
      mode: "Ready",
      modeTimer: 0,
      pulled: false,
    },
  ];
}

// Per-team seat assignments at match start. Omitted / undefined seats default
// to today's "Player vs CPU" layout (home P1 = home-1, everyone else bot).
// Pass `null` explicitly to take a seat away — that's how CPU-vs-CPU works:
// `{ home: { P1: null }, away: { P1: null } }`.
export type HumanSeatsConfig = {
  home?: { P1?: PlayerId | null; P2?: PlayerId | null };
  away?: { P1?: PlayerId | null; P2?: PlayerId | null };
};

export function createInitialControl(humanSeats?: HumanSeatsConfig): TeamControlState[] {
  const homeP1 =
    humanSeats?.home && "P1" in humanSeats.home ? (humanSeats.home.P1 ?? null) : playerId("home-1");
  const homeP2 = humanSeats?.home && "P2" in humanSeats.home ? (humanSeats.home.P2 ?? null) : null;
  const awayP1 = humanSeats?.away && "P1" in humanSeats.away ? (humanSeats.away.P1 ?? null) : null;
  const awayP2 = humanSeats?.away && "P2" in humanSeats.away ? (humanSeats.away.P2 ?? null) : null;

  const homeControl: TeamControlState = {
    teamId: "home",
    controlledByHuman: { P1: homeP1, P2: homeP2 },
    lastManualSwitchTick: 0,
    manualGoalieSeat: null,
    goaliePullToggleHeldLastTick: false,
    tactic: "balanced",
  };

  const awayControl: TeamControlState = {
    teamId: "away",
    controlledByHuman: { P1: awayP1, P2: awayP2 },
    lastManualSwitchTick: 0,
    manualGoalieSeat: null,
    goaliePullToggleHeldLastTick: false,
    tactic: "balanced",
  };

  return [homeControl, awayControl];
}
